/**
 * Goldielocks Canonical Schema — Single Source of Truth (frontend mirror)
 * All matchable fields must use these enums exactly.
 * Provider-agnostic: Phase C (Dream Builder) maps free text → these keys.
 */

// ─── Amenities ─────────────────────────────────────────────────────────────────
export const AMENITY_LOCATION = [
  { id: 'golf_course',     label: 'Golf Course' },
  { id: 'riverfront',      label: 'Riverfront' },
  { id: 'lakefront',       label: 'Lakefront' },
  { id: 'oceanfront',      label: 'Oceanfront' },
  { id: 'waterfront',      label: 'Waterfront' },
  { id: 'mountain_view',   label: 'Mountain View' },
  { id: 'city_view',       label: 'City View' },
  { id: 'vineyard_view',   label: 'Vineyard View' },
  { id: 'park_adjacent',   label: 'Park Adjacent' },
  { id: 'trail_access',    label: 'Trail Access' },
  { id: 'ski_access',      label: 'Ski Access' },
  { id: 'beach_access',    label: 'Beach Access' },
  { id: 'marina_access',   label: 'Marina Access' },
];

export const AMENITY_COMMUNITY = [
  { id: 'gated',               label: 'Gated' },
  { id: 'senior_55_plus',      label: '55+ Community' },
  { id: 'planned_development',  label: 'Planned Development' },
  { id: 'country_club',        label: 'Country Club' },
  { id: 'hoa_amenities',       label: 'HOA Amenities' },
  { id: 'walkable',            label: 'Walkable' },
  { id: 'resort_style',        label: 'Resort-Style' },
];

export const AMENITY_PROPERTY = [
  { id: 'pool',             label: 'Pool' },
  { id: 'spa',              label: 'Spa / Hot Tub' },
  { id: 'outdoor_kitchen',  label: 'Outdoor Kitchen' },
  { id: 'fire_pit',         label: 'Fire Pit' },
  { id: 'deck',             label: 'Deck' },
  { id: 'patio',            label: 'Patio' },
  { id: 'balcony',          label: 'Balcony' },
  { id: 'landscaped',       label: 'Landscaped' },
  { id: 'fenced_yard',      label: 'Fenced Yard' },
];

export const AMENITY_LAND = [
  { id: 'horse_property',      label: 'Horse Property' },
  { id: 'agricultural',        label: 'Agricultural' },
  { id: 'vineyard_potential',  label: 'Vineyard Potential' },
  { id: 'orchard',             label: 'Orchard' },
  { id: 'wooded',              label: 'Wooded' },
  { id: 'off_grid_capable',    label: 'Off-Grid Capable' },
];

export const AMENITY_UTILITIES = [
  { id: 'solar',              label: 'Solar Panels' },
  { id: 'ev_charger',         label: 'EV Charger' },
  { id: 'smart_home',         label: 'Smart Home' },
  { id: 'generator',          label: 'Generator' },
  { id: 'high_speed_internet', label: 'High-Speed Internet' },
];

export const AMENITY_SPECIAL = [
  { id: 'exceptional_view',      label: 'Exceptional View' },
  { id: 'private_secluded',      label: 'Private & Secluded' },
  { id: 'end_of_road_privacy',   label: 'End-of-Road Privacy' },
  { id: 'historic',              label: 'Historic' },
  { id: 'luxury_estate',         label: 'Luxury Estate' },
];

export const AMENITY_SECTIONS = [
  { key: 'location',  label: 'Views & Location',   icon: 'Mountain',  items: AMENITY_LOCATION },
  { key: 'community', label: 'Community',           icon: 'Users',     items: AMENITY_COMMUNITY },
  { key: 'property',  label: 'Property Features',   icon: 'Home',      items: AMENITY_PROPERTY },
  { key: 'land',      label: 'Land & Acreage',      icon: 'Leaf',      items: AMENITY_LAND },
  { key: 'utilities', label: 'Utilities & Tech',    icon: 'Zap',       items: AMENITY_UTILITIES },
  { key: 'special',   label: 'Special Features',    icon: 'Sparkles',  items: AMENITY_SPECIAL },
];

// ─── Style ─────────────────────────────────────────────────────────────────────
export const STYLE_OPTIONS = [
  { id: 'chalet',            label: 'Chalet' },
  { id: 'cabin',             label: 'Cabin' },
  { id: 'log_home',          label: 'Log Home' },
  { id: 'a_frame',           label: 'A-Frame' },
  { id: 'farmhouse',         label: 'Farmhouse' },
  { id: 'modern_farmhouse',  label: 'Modern Farmhouse' },
  { id: 'ranch',             label: 'Ranch' },
  { id: 'craftsman',         label: 'Craftsman' },
  { id: 'contemporary',      label: 'Contemporary' },
  { id: 'mid_century',       label: 'Mid-Century' },
  { id: 'minimalist',        label: 'Minimalist' },
  { id: 'industrial',        label: 'Industrial' },
  { id: 'steel_frame',       label: 'Steel Frame' },
  { id: 'glass_house',       label: 'Glass House' },
  { id: 'mediterranean',     label: 'Mediterranean' },
  { id: 'spanish',           label: 'Spanish' },
  { id: 'tudor',             label: 'Tudor' },
  { id: 'colonial',          label: 'Colonial' },
  { id: 'victorian',         label: 'Victorian' },
];

// ─── Kitchen ───────────────────────────────────────────────────────────────────
export const KITCHEN_LAYOUTS = [
  { id: 'open',            label: 'Open Concept' },
  { id: 'separate',        label: 'Separate Kitchen' },
  { id: 'chefs_kitchen',   label: "Chef's Kitchen" },
  { id: 'butlers_pantry',  label: "Butler's Pantry" },
  { id: 'walk_in_pantry',  label: 'Walk-In Pantry' },
];

export const KITCHEN_ISLANDS = [
  { id: 'island',          label: 'Island' },
  { id: 'double_island',   label: 'Double Island' },
  { id: 'breakfast_bar',   label: 'Breakfast Bar' },
];

export const KITCHEN_COUNTERTOPS = [
  { id: 'quartz',          label: 'Quartz' },
  { id: 'granite',         label: 'Granite' },
  { id: 'marble',          label: 'Marble' },
  { id: 'butcher_block',   label: 'Butcher Block' },
  { id: 'concrete',        label: 'Concrete' },
];

export const KITCHEN_APPLIANCES = [
  { id: 'appliances_included',  label: 'Appliances Included' },
  { id: 'high_end_appliances',  label: 'High-End Appliances' },
  { id: 'gas_range',            label: 'Gas Range' },
  { id: 'double_oven',          label: 'Double Oven' },
  { id: 'wine_fridge',          label: 'Wine Fridge' },
  { id: 'built_in_appliances',  label: 'Built-In Appliances' },
];

export const KITCHEN_FEATURES = [
  { id: 'custom_cabinetry',   label: 'Custom Cabinetry' },
  { id: 'smart_appliances',   label: 'Smart Appliances' },
  { id: 'pot_filler',         label: 'Pot Filler' },
];

export const KITCHEN_SECTIONS = [
  { key: 'layout',      label: 'Layout',      items: KITCHEN_LAYOUTS },
  { key: 'island',      label: 'Island',      items: KITCHEN_ISLANDS },
  { key: 'countertops', label: 'Countertops', items: KITCHEN_COUNTERTOPS },
  { key: 'appliances',  label: 'Appliances',  items: KITCHEN_APPLIANCES },
  { key: 'features',    label: 'Features',    items: KITCHEN_FEATURES },
];

// ─── Furnishing ────────────────────────────────────────────────────────────────
export const FURNISHING_STATUS = [
  { id: 'fully_furnished',    label: 'Fully Furnished' },
  { id: 'partially_furnished', label: 'Partially Furnished' },
  { id: 'unfurnished',        label: 'Unfurnished' },
];

export const FURNISHING_INCLUDES = [
  { id: 'appliances',        label: 'Appliances Included' },
  { id: 'washer_dryer',      label: 'Washer & Dryer' },
  { id: 'outdoor_furniture', label: 'Outdoor Furniture' },
  { id: 'turnkey_ready',     label: 'Turnkey Ready' },
  { id: 'str_ready',         label: 'STR / Airbnb Ready' },
];

// ─── Area types (international location) ──────────────────────────────────────
export const AREA_TYPES = [
  { id: 'city',          label: 'City' },
  { id: 'neighborhood',  label: 'Neighborhood' },
  { id: 'town',          label: 'Town' },
  { id: 'village',       label: 'Village' },
  { id: 'enclave',       label: 'Enclave' },
  { id: 'district',      label: 'District' },
  { id: 'region',        label: 'Region' },
];

// ─── Empty canonical v3 profile init ─────────────────────────────────────────
export const EMPTY_AMENITIES = {
  location: [], community: [], property: [], land: [], utilities: [], special: [],
};

export const EMPTY_KITCHEN = {
  layout: [], island: [], countertops: [], appliances: [], features: [],
};

export const EMPTY_FURNISHING = { status: '', includes: [] };

// ─── Phase C: Dream Builder provider config (swappable) ───────────────────────
// Provider is read from env at runtime — never hardcoded here.
// Supported: 'claude' | 'openai' | 'emergent'
// Model name is set via REACT_APP_DREAM_BUILDER_MODEL env var.
export const DREAM_BUILDER_PROVIDER = process.env.REACT_APP_DREAM_BUILDER_PROVIDER || 'claude';
export const DREAM_BUILDER_MODEL    = process.env.REACT_APP_DREAM_BUILDER_MODEL    || 'claude-haiku-4-5';
